import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { RotatingLines } from "react-loader-spinner";
import Select from 'react-select';
import { debounce } from 'lodash';

export default function CourseMapping() {
  // State management
  const [state, setState] = useState({
    courses: [],
    classes: [],
    mappings: [],
    loading: true,
    formSubmitting: false,
    formData: {
      classCourseMappingId: 0,
      classId: 0,
      courseId: 0,
      className: "",
      coursename: "",
      mappingStatus: true
    },
    searchQuery: "",
    filterStatus: "all",
    currentPage: 0,
    itemsPerPage: 5
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchCourseMappings();
    fetchCourses();
    fetchClasses();
  }, []);

  // API calls
  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Course/SuperAdminCourseMasterList`, {
        headers: getHeaders(),
      });
      updateState({ courses: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching courses:", error);
      Swal.fire("Error!", "Failed to load courses.", "error");
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}class/ClassMasterList`, {
        headers: getHeaders(),
      });
      updateState({ classes: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching classes:", error);
      Swal.fire("Error!", "Failed to load classes.", "error");
    }
  };

  const fetchCourseMappings = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(`${API_LoginURL}classcoursemapping/ClassCourseMappingDetailList`, {
        headers: getHeaders(),
      });
      updateState({
        mappings: Array.isArray(response.data) ? response.data : [],
        loading: false
      });
    } catch (error) {
      console.error("Error fetching course mappings:", error);
      Swal.fire("Error!", "Failed to load course mappings.", "error");
      updateState({ loading: false });
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token")
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: value
      }
    });
  };

  const handleClassChange = (selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        classId: selectedOption ? selectedOption.value : 0,
        className: selectedOption ? selectedOption.label : ""
      }
    });
  };

  const handleCourseChange = (selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        courseId: selectedOption ? selectedOption.value : 0,
        coursename: selectedOption ? selectedOption.label : ""
      }
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      updateState({ searchQuery: query, currentPage: 0 });
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      filterStatus: e.target.value,
      currentPage: 0
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        classCourseMappingId: 0,
        classId: 0,
        courseId: 0,
        className: "",
        coursename: "",
        mappingStatus: true
      }
    });
  };

  const handleEdit = (mapping) => {
    updateState({
      formData: {
        ...mapping,
        mappingStatus: mapping.mappingStatus !== false
      }
    });
  };

  const handleDelete = async (classCourseMappingId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.post(
        `${API_LoginURL}classcoursemapping/DeleteClassCourseMappingDetails`,
        { classCourseMappingId:classCourseMappingId },
        { headers: getHeaders() }
      );
      updateState({
        mappings: state.mappings.filter(item => item.classCourseMappingId !== classCourseMappingId)
      });
      Swal.fire("Deleted!", "Course-Class mapping has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting mapping:", error);
      Swal.fire("Error", "Failed to delete mapping", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    updateState({ formSubmitting: true });
    try {
      const response = await axios.post(
        `${API_LoginURL}classcoursemapping/ClassCourseMappingAddEdit`,
        state.formData,
        { headers: getHeaders() }
      );

      Swal.fire({
        icon: response.data.response === "SUCCESS" ? "success" : "error",
        title: response.data.response === "SUCCESS" ? "Success" : "Error",
        text: response.data.response === "SUCCESS"
          ? "Mapping saved successfully"
          : response.data.response,
      });

      if (response.data.response === "SUCCESS") {
        fetchCourseMappings();
        document.getElementById("closeMappingModal").click();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving mapping:", error);
      Swal.fire("Error", "Failed to save mapping", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  // Filter and pagination
  const filteredMappings = state.mappings.filter(item => {
    const matchesSearch =
      item.className.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      item.coursename.toLowerCase().includes(state.searchQuery.toLowerCase());

    const matchesStatus =
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.mappingStatus) ||
      (state.filterStatus === "inactive" && !item.mappingStatus);

    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredMappings.length / state.itemsPerPage);
  const displayedMappings = filteredMappings.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">Course-Class Mapping</h4>

                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Class or Course"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>

                      {/* Filter */}
                      {/* <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={state.filterStatus}
                          onChange={handleFilterChange}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="all">All Status</option>
                          <option value="inactive">Active</option>
                          <option value="active">Inactive</option>
                        </select>
                      </div> */}

                      {/* Add Mapping Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#mappingModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Mapping</span>
                      </button>
                    </div>
                  </div>

                  {/* Mappings Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <RotatingLines color="#007bff" width={50} />
                        <p className="mt-3 ms-3">Loading mappings...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped">
                          <thead className="table-primary">
                            <tr>
                              <th width="10%">ID</th>
                              <th width="30%">Class</th>
                              <th width="30%">Course</th>
                              {/* <th width="10%">Status</th> */}
                              <th width="20%" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedMappings.length > 0 ? (
                              displayedMappings.map((item) => (
                                <tr key={item.classCourseMappingId}>
                                  <td>{item.classCourseMappingId}</td>
                                  <td>{item.className}</td>
                                  <td>{item.coursename}</td>

                                  {/* <td
                                    style={{
                                      color: item.mappingStatus
                                        ? 
                                       "red":"green",
                                    }}
                                  >
                                    {item.mappingStatus ? "Inactive":"Active"}

                                  </td> */}
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#mappingModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.classCourseMappingId)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No mappings"
                                      style={{ opacity: 0.5, width: '60px' }}
                                    />
                                    <p className="mt-3 text-muted">No mappings found</p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#mappingModal"
                                      onClick={resetForm}
                                    >
                                      Add New Mapping
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedMappings.length > 0 && (
                          <ReactPaginate
                            previousLabel={<i className="fas fa-chevron-left"></i>}
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination justify-content-center mt-4"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mapping Modal */}
      <div className="modal fade" id="mappingModal" tabIndex="-1" aria-labelledby="mappingModalLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="mappingModalLabel">
                {state.formData.classCourseMappingId > 0 ? 'Edit Mapping' : 'Add New Mapping'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form>
              <div className="row decrease_gap g-4">
                <div className="mb-3">
                  <label htmlFor="classId" className="form-label required">Class</label>
                  <Select
                    id="classId"
                    options={state.classes.map(cls => ({
                      value: cls.classId,
                      label: cls.className
                    }))}
                    value={state.classes
                      .map(cls => ({
                        value: cls.classId,
                        label: cls.className
                      }))
                      .find(option => option.label === state.formData.className) || null}
                    onChange={handleClassChange}
                    placeholder="Select Class"
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="courseId" className="form-label required">Course</label>
                  <Select
                    id="courseId"
                    options={state.courses.map(course => ({
                      value: course.courseId,
                      label: course.courseName
                    }))}
                    value={state.courses
                      .map(course => ({
                        value: course.courseId,
                        label: course.courseName
                      }))
                      .find(option => option.value === state.formData.courseId) || null}
                    onChange={handleCourseChange}
                    placeholder="Select Course"
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                  />
                </div>
                {/* {state.formData.classCourseMappingId > 0 && (
                  <div className="mb-3">
                    <label htmlFor="mappingStatus" className="form-label">Status</label>
                    <select
                      className="form-select"
                      id="mappingStatus"
                      name="mappingStatus"
                      value={state.formData.mappingStatus}
                      onChange={handleInputChange}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                )} */}
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" id="closeMappingModal" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}